import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Shield, Zap, MessageSquare, ClipboardList, type LucideIcon } from 'lucide-react';

export type OperatorTab = 'fleet' | 'actions' | 'chat' | 'audit';

interface TabDef {
  key: OperatorTab;
  label: string;
  Icon: LucideIcon;
  route: string;
}

const TABS: TabDef[] = [
  { key: 'fleet',   label: 'Fleet',   Icon: Shield,        route: '/sentinel-manager' },
  { key: 'actions', label: 'Actions', Icon: Zap,           route: '/sentinel-manager/inbox' },
  { key: 'chat',    label: 'Chat',    Icon: MessageSquare, route: '/sentinel-manager/chat' },
  { key: 'audit',   label: 'Audit',   Icon: ClipboardList, route: '/sentinel-manager/notifications/audit' },
];

interface OperatorBottomNavProps {
  activeTab: OperatorTab;
  onTabChange: (tab: OperatorTab, route: string) => void;
  badges?: Partial<Record<OperatorTab, number>>;
}

export function OperatorBottomNav({ activeTab, onTabChange, badges = {} }: OperatorBottomNavProps) {
  const handleTabClick = useCallback(
    (tab: TabDef) => {
      onTabChange(tab.key, tab.route);
    },
    [onTabChange],
  );

  const prevBadgesRef = useRef<Partial<Record<OperatorTab, number>>>({});
  const [bouncingTabs, setBouncingTabs] = useState<Set<OperatorTab>>(new Set());

  useEffect(() => {
    const prev = prevBadgesRef.current;
    const nowBouncing = new Set<OperatorTab>();
    for (const tab of TABS) {
      const oldVal = prev[tab.key] ?? 0;
      const newVal = badges[tab.key] ?? 0;
      if (newVal > oldVal) nowBouncing.add(tab.key);
    }
    prevBadgesRef.current = { ...badges };
    if (nowBouncing.size === 0) return;
    setBouncingTabs(nowBouncing);
    const timer = setTimeout(() => setBouncingTabs(new Set()), 300);
    return () => clearTimeout(timer);
  }, [badges]);

  return (
    <nav
      className="operator-bottom-nav"
      aria-label="Operator navigation"
      data-testid="operator-bottom-nav"
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab.key;
        const badge = badges[tab.key];
        const isBouncing = bouncingTabs.has(tab.key);

        return (
          <button
            key={tab.key}
            className="operator-nav-tab"
            aria-current={isActive ? 'page' : undefined}
            aria-label={badge ? `${tab.label}, ${badge} pending` : tab.label}
            onClick={() => handleTabClick(tab)}
            data-testid={`operator-tab-${tab.key}`}
          >
            <span className="operator-nav-tab-icon" aria-hidden="true"><tab.Icon size={22} strokeWidth={1.8} /></span>
            <span className="operator-nav-tab-label">{tab.label}</span>
            {badge != null && badge > 0 && (
              <span
                className={`operator-nav-badge${isBouncing ? ' operator-nav-badge--bounce' : ''}`}
                data-urgency={tab.key === 'fleet' ? 'danger' : 'warning'}
                aria-hidden="true"
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
            <span className="operator-nav-glow" aria-hidden="true" />
          </button>
        );
      })}
    </nav>
  );
}
